import { useCallback, useEffect, useRef, useState } from 'react';
import { GameFrame, PromptLine, pick, useGame, useRoundTimer, type GameProps } from '../kit/shell';
import { BLOCK_COLOURS, BLOCK_COPY } from '../phrases';
import { ORDER_SEPARATOR } from '../trials';
import { KINDS } from '../../concepts/kinds';

type Row = { label: string; items: string[] };

const norm = (s: string) => s.trim().toLowerCase();

export default function BlockCopy(props: GameProps) {
  const game = useGame(props);
  const rows = ((game.config?.sequences ?? []) as Row[]).filter((r) => r?.label && Array.isArray(r.items) && [4, 9, 16].includes(r.items.length) && r.items.every((c) => BLOCK_COLOURS[norm(c)]));
  const hides = game.str('modelStays', 'stays') === 'hides';
  const [row, setRow] = useState<Row | null>(null);
  const [cells, setCells] = useState<(string | null)[]>([]);
  const [brush, setBrush] = useState<string | null>(null);
  const [showModel, setShowModel] = useState(true);
  const [trial, setTrial] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const busy = useRef(false);

  const start = useCallback(async () => {
    if (rows.length === 0) return;
    const next = pick(rows);
    const target = next.items.map(norm);
    setDone(false);
    setBrush(null);
    setShowModel(true);
    const id = await game.register({ label: next.label, size: Math.sqrt(target.length) }, { value: target.join(ORDER_SEPARATOR) });
    if (!id) return;
    setRow({ label: next.label, items: target });
    setCells(target.map(() => null));
    setTrial(id);
  }, [rows.map((r) => r.items.join()).join('|')]);

  useEffect(() => { if (game.config) void start(); }, [game.config, start]);
  useEffect(() => {
    if (!row) return;
    game.say(BLOCK_COPY);
    if (!hides) return;
    const t = window.setTimeout(() => setShowModel(false), 5000);
    return () => window.clearTimeout(t);
  }, [row, hides]);

  async function paint(i: number) {
    if (!row || !brush || done || busy.current) return;
    if (game.errorless && row.items[i] !== brush) return;
    const next = [...cells];
    next[i] = brush;
    setCells(next);
    if (!next.every(Boolean) || !trial) return;
    busy.current = true;
    const v = await game.respond(trial, next.join(ORDER_SEPARATOR));
    if (v?.correct) { setDone(true); busy.current = false; return; }
    setShowModel(true);
    const fresh = await game.register({ label: row.label, retry: true }, { value: row.items.join(ORDER_SEPARATOR) });
    busy.current = false;
    if (fresh) { setTrial(fresh); setCells(row.items.map((c, k) => (next[k] === c ? c : null))); }
  }

  const fading = useRoundTimer(done, game.transitionMs, () => { void start(); }, 1200);
  const n = row ? Math.sqrt(row.items.length) : 2;
  const palette = row ? [...new Set(row.items)] : [];

  const grid = (values: (string | null)[], onCell?: (i: number) => void, label?: string) => (
    <div className="g-blocks" style={{ gridTemplateColumns: `repeat(${n}, 1fr)` }} role={onCell ? 'group' : 'img'} aria-label={label}>
      {values.map((c, i) => (
        onCell ? (
          <button key={i} type="button" className="g-block" aria-label={`Square ${i + 1}${c ? `, ${c}` : ''}`} style={{ background: c ? BLOCK_COLOURS[c] : undefined }} onClick={() => void paint(i)} />
        ) : (
          <span key={i} className="g-block" style={{ background: c ? BLOCK_COLOURS[c] : undefined }} />
        )
      ))}
    </div>
  );

  return (
    <GameFrame game={game} empty={rows.length === 0} emptyText={`${KINDS.blockCopy.describes} Edit it to add a pattern.`} onResume={() => { void start(); }} breakTrial={trial}>
      {row && (
        <div className={`g-board g-copy${fading ? ' is-fading' : ''}`}>
          <PromptLine game={game} text="Make one the same" />
          <div className="g-copy-row">
            <div className="g-copy-side">
              <span className="field-label">Copy this</span>
              {showModel ? grid(row.items, undefined, 'The pattern') : <div className="g-blocks g-hidden-model" aria-label="Pattern hidden">?</div>}
            </div>
            <div className="g-copy-side">
              <span className="field-label">Your blocks</span>
              {grid(cells, paint, 'Your grid')}
            </div>
          </div>
          <div className="g-palette" role="group" aria-label="Colours">
            {palette.map((c) => (
              <button key={c} type="button" className={`g-swatch${brush === c ? ' is-held' : ''}`} aria-pressed={brush === c} aria-label={c} style={{ background: BLOCK_COLOURS[c] }} onClick={() => { setBrush(c); game.say(c); }}>
                <span className="g-swatch-word">{c}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </GameFrame>
  );
}
